import React from 'react';
import {Box} from '@mui/material';
import type {Avatar, Voice} from '@contentlab/api/avatars';
import type {DraftAiAvatarProject} from '@contentlab/stores/contentLabStore';
import {AvatarKind} from '@contentlab/types/aiAvatars';
import type {AvatarFilter} from '@contentlab/types/avatarFilters';
import EmptyAiAvatarContent from '../../empty-ai-avatar-content';
import AvatarEditor from './AvatarEditor';

interface Props {
  testID?: string;
  hasUploadedVideo?: boolean;
  selectedAvatar?: Avatar | null;
  onAvatarSelect?: (avatar: Avatar) => void;
  isSaving?: boolean;
  avatarSpec: DraftAiAvatarProject['avatarSpec'];
  selectedAvatarKind: AvatarKind | null;
  onSelectKind: (kind: AvatarKind) => void;
  onOpenConfig?: () => void;
  voiceOnlySelectedId?: string | null;
  customSelectedVoiceId?: string | null;
  onVoiceOnlySelectVoice?: (voice: Voice) => void;
  onCustomSelectVoice?: (voice: Voice) => void;
  onCustomSelectImage?: (file: File) => void | Promise<void>;
  isAvatarPreviewMuted?: boolean;
  onAvatarPreviewMuteChange?: (avatar: Avatar, muted: boolean) => void;
  avatarFilters?: AvatarFilter[];
  onAvatarFilterChange?: (id: string, selectedOptionId: string[]) => void;
  avatars: Avatar[];
  voices: Voice[];
  isLoadingAvatarsOrVoices?: boolean;
}

function AvatarsTab({
  testID = 'avatars-tab',
  hasUploadedVideo,
  selectedAvatar,
  onAvatarSelect,
  isSaving,
  avatarSpec,
  selectedAvatarKind,
  onSelectKind,
  onOpenConfig,
  voiceOnlySelectedId,
  customSelectedVoiceId,
  onVoiceOnlySelectVoice,
  onCustomSelectVoice,
  onCustomSelectImage,
  isAvatarPreviewMuted,
  onAvatarPreviewMuteChange,
  avatarFilters,
  onAvatarFilterChange,
  avatars,
  voices,
  isLoadingAvatarsOrVoices,
}: Props) {
  return (
    <Box
      data-testid={testID}
      sx={{
        padding: '0 0 0 24px',
        position: 'relative',
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      {!hasUploadedVideo ? (
        <EmptyAiAvatarContent />
      ) : (
        <Box sx={{position: 'relative', height: '100%'}}>
          <Box
            sx={{
              transition: 'filter 120ms ease',
              height: '100%',
            }}
          >
            <AvatarEditor
              onAvatarSelect={onAvatarSelect}
              selectedAvatar={selectedAvatar ?? null}
              avatarSpec={avatarSpec}
              selectedAvatarKind={selectedAvatarKind}
              onSelectKind={onSelectKind}
              onOpenConfig={onOpenConfig}
              voiceOnlySelectedId={voiceOnlySelectedId}
              customSelectedVoiceId={customSelectedVoiceId}
              onVoiceOnlySelectVoice={onVoiceOnlySelectVoice}
              onCustomSelectVoice={onCustomSelectVoice}
              onCustomSelectImage={onCustomSelectImage}
              isAvatarPreviewMuted={isAvatarPreviewMuted}
              onAvatarPreviewMuteChange={onAvatarPreviewMuteChange}
              avatarFilters={avatarFilters}
              onAvatarFilterChange={onAvatarFilterChange}
              avatars={avatars}
              voices={voices}
              isLoadingAvatarsOrVoices={isLoadingAvatarsOrVoices}
            />
          </Box>
          {isSaving ? (
            <Box sx={{position: 'absolute', inset: 0, pointerEvents: 'none'}} />
          ) : null}
        </Box>
      )}
    </Box>
  );
}

export default AvatarsTab;
